export interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
}

export enum UserRole {
  LISTING_AGENT = 'listing_agent',
  LANDLORD = 'landlord',
  RENTER_AGENT = 'renter_agent',
  PROPERTY_MANAGER = 'property_manager',
}

export interface UserRoleInfo {
  role: UserRole;
  label: string;
  description: string;
  icon: string;
}

export interface OwnerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company?: string;
}

export interface AgreementAcceptance {
  accepted: boolean;
  timestamp: Date;
  version: string;
}

export enum ScreeningPackage {
  BASIC = 'basic',
  COMPREHENSIVE = 'comprehensive',
}

export interface PackageInfo {
  type: ScreeningPackage;
  name: string;
  price: number;
  features: string[];
  recommended: boolean;
}

export enum PaymentResponsibility {
  APPLICANT = 'applicant',
  LANDLORD = 'landlord',
}

export enum ApplicationType {
  RENTSPREE = 'rentspree',
  CUSTOM = 'custom',
}

export interface PayeeConfiguration {
  paymentResponsibility: PaymentResponsibility;
  applicationType: ApplicationType;
  customApplicationName?: string;
}

export interface LinkSharingInfo {
  applicationLink: string;
  qrCodeUrl: string;
  emailsSent: string[];
  socialPlatforms: string[];
}

export interface TenantScreeningFormData {
  address: Address;
  userRole: UserRole;
  ownerInfo?: OwnerInfo;
  agreement: AgreementAcceptance;
  selectedPackage: ScreeningPackage;
  payeeConfig: PayeeConfiguration;
  linkSharing: LinkSharingInfo;
  createdAt: Date;
  updatedAt: Date;
}

export interface TenantScreeningFormState {
  address?: Partial<Address>;
  userRole?: UserRole;
  ownerInfo?: Partial<OwnerInfo>;
  agreement?: Partial<AgreementAcceptance>;
  selectedPackage?: ScreeningPackage;
  payeeConfig?: Partial<PayeeConfiguration>;
  linkSharing?: Partial<LinkSharingInfo>;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface StepValidation {
  isValid: boolean;
  errors: Record<string, string>;
}

export interface TenantScreeningStep {
  stepNumber: number;
  title: string;
  description: string;
  progressPercentage: number;
  isCompleted: boolean;
  isRequired: boolean;
  validation: StepValidation;
}

export interface TenantScreeningState {
  currentStep: number;
  formData: Partial<TenantScreeningFormData>;
  steps: TenantScreeningStep[];
  isSubmitting: boolean;
  error: string | null;
}

export const USER_ROLES: UserRoleInfo[] = [
  {
    role: UserRole.LISTING_AGENT,
    label: "I'm a listing agent",
    description: 'Representing the landlord',
    icon: 'agent',
  },
  {
    role: UserRole.LANDLORD,
    label: "I'm the landlord",
    description: 'Listing my own property',
    icon: 'landlord',
  },
  {
    role: UserRole.RENTER_AGENT,
    label: "I'm a renter agent",
    description: 'Representing the renter',
    icon: 'renter',
  },
  {
    role: UserRole.PROPERTY_MANAGER,
    label: "I'm a property manager",
    description: 'Managing on behalf of an owner',
    icon: 'manager',
  },
];

export const SCREENING_PACKAGES: PackageInfo[] = [
  {
    type: ScreeningPackage.BASIC,
    name: 'Basic Package',
    price: 39.99,
    features: [
      'Rental Application',
      'Credit Report & Score',
      'Basic Criminal Background Check',
    ],
    recommended: false,
  },
  {
    type: ScreeningPackage.COMPREHENSIVE,
    name: 'Comprehensive Package',
    price: 49.99,
    features: [
      'Rental Application',
      'Credit Report & Score',
      'Federal Background Check',
      'Jurisdiction Background Check',
      'Eviction Report',
      'Income Verification',
      'Auto Reference Check',
    ],
    recommended: true,
  },
];

export const STEP_PROGRESS_PERCENTAGES: Record<number, number> = {
  1: 20,
  2: 40,
  3: 60,
  4: 60,
  5: 80,
  6: 100,
  7: 100,
};

export const shouldShowOwnerInfoStep = (role: UserRole): boolean => {
  return role !== UserRole.LANDLORD;
};

export const calculateStepNumber = (
  currentStep: number,
  userRole?: UserRole
): number => {
  if (!userRole) return currentStep;

  if (currentStep >= 3 && !shouldShowOwnerInfoStep(userRole)) {
    return currentStep + 1;
  }

  return currentStep;
};

export const getTotalSteps = (userRole?: UserRole): number => {
  return shouldShowOwnerInfoStep(userRole || UserRole.LISTING_AGENT) ? 7 : 6;
};

export const calculateCurrentProgress = (
  currentStep: number,
  userRole?: UserRole
): number => {
  if (!userRole) return 0;

  // Progress percentages for each step
  const progressMap: Record<number, number> = {
    1: 20, // Address - 20%
    2: 40, // Role - 40%
    3: 60, // Owner Info - 60%
    4: 60, // Agreement - 60% (same as step 3)
    5: 80, // Package - 80%
    6: 100, // Payee - 100%
    7: 100, // Success - 100%
  };

  // If user is landlord, adjust step numbers since step 3 is skipped
  if (!shouldShowOwnerInfoStep(userRole)) {
    if (currentStep >= 4) {
      // Steps 4, 5, 6, 7 become 3, 4, 5, 6 in the flow
      const adjustedProgressMap: Record<number, number> = {
        1: 20, // Address - 20%
        2: 40, // Role - 40%
        3: 60, // Agreement - 60% (skipped owner info)
        4: 80, // Package - 80%
        5: 100, // Payee - 100%
        6: 100, // Success - 100%
      };
      return adjustedProgressMap[currentStep - 1] || 0;
    }
  }

  return progressMap[currentStep] || 0;
};
